/**
 * Pure geography helpers shared by bindings (copy), the set_geography
 * mutation (re-author flagging) and sizing. No zod, no I/O — src/domain is
 * imported by the web build.
 */

import type { AudienceSegment, GeoArea } from './types.js';

/** "Minneapolis, MN" -> "Minneapolis". Falls back to the whole label. */
function cityPart(label: string): string {
  return label.split(',')[0]?.trim() || label.trim();
}

/**
 * Human labels for a list of areas. State suffixes are dropped when the city
 * parts are unique, because "Minneapolis, MN, Charleston, SC" reads as comma
 * soup; if two areas share a city name the full labels are kept.
 */
export function areaDisplayNames(areas: Pick<GeoArea, 'label'>[]): string[] {
  const labels = areas.map((a) => a.label.trim()).filter(Boolean);
  if (labels.length <= 1) return labels;
  const cities = labels.map(cityPart);
  const unique = new Set(cities.map((c) => c.toLowerCase())).size === cities.length;
  return unique ? cities : labels;
}

/** "A", "A and B", "A, B and C" (semicolons when the names contain commas). */
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names.join('');
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  if (names.some((n) => n.includes(','))) {
    return `${names.slice(0, -1).join('; ')}; and ${names[names.length - 1]}`;
  }
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * A noun phrase, so it reads after "across", "Targeting" and "in":
 * "a nationwide footprint, prioritizing New York, Charleston and Minneapolis" /
 * "a nationwide footprint".
 */
export function nationalSummary(areas: Pick<GeoArea, 'label'>[]): string {
  const names = areas.length === 1 ? areas.map((a) => cityPart(a.label)).filter(Boolean) : areaDisplayNames(areas);
  return names.length > 0
    ? `a nationwide footprint, prioritizing ${joinNames(names)}`
    : 'a nationwide footprint';
}

/**
 * {{geo.areas}} for a national proposal. It is used as a SHORT PLACE NAME
 * ("reach in {{geo.areas}}", "Streaming Audio in {{geo.areas}}"), so the
 * priority markets never appear in it — they are a weighting, not the target.
 */
export const NATIONAL_PLACE_NAME = 'the U.S.';

/** Alternate names a place is commonly written under in generated copy. */
const PLACE_ALIASES: Array<{ pattern: RegExp; aliases: string[] }> = [
  { pattern: /minneapolis|st\.? paul/i, aliases: ['Twin Cities'] },
  { pattern: /new york/i, aliases: ['NYC', 'New York City', 'Manhattan', 'Brooklyn'] },
  { pattern: /san francisco|oakland|san jose/i, aliases: ['Bay Area'] },
  { pattern: /dallas|fort worth/i, aliases: ['DFW', 'Dallas-Fort Worth'] },
];

/** Every string a piece of copy might use to name this area. */
export function areaNameVariants(label: string): string[] {
  const out = new Set<string>();
  const full = label.trim();
  if (full) out.add(full);
  const city = cityPart(full);
  if (city) out.add(city);
  // "Minneapolis-St. Paul" -> "Minneapolis"
  const head = city.split(/[-–—/]/)[0]?.trim();
  if (head && head.length >= 4) out.add(head);
  for (const { pattern, aliases } of PLACE_ALIASES) {
    if (pattern.test(full)) aliases.forEach((a) => out.add(a));
  }
  return [...out].filter((v) => v.length >= 3);
}

function mentions(text: string, variant: string): boolean {
  const escaped = variant.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^A-Za-z])${escaped}([^A-Za-z]|$)`, 'i').test(text);
}

/** The given labels that this segment's authored text names. */
export function geographyNamedBySegment(segment: AudienceSegment, labels: string[]): string[] {
  const text = [segment.name, segment.description, ...segment.definingSignals, segment.personFilterPrompt ?? '']
    .filter(Boolean)
    .join('\n');
  return labels.filter((label) => areaNameVariants(label).some((v) => mentions(text, v)));
}

function labelKey(areas: Pick<GeoArea, 'label'>[]): string {
  return areas
    .map((a) => a.label.trim().toLowerCase())
    .sort()
    .join('|');
}

export function sameAreaSet(a: Pick<GeoArea, 'label'>[], b: Pick<GeoArea, 'label'>[]): boolean {
  return labelKey(a) === labelKey(b);
}

/**
 * After a geography change: which segments' COPY names the previous areas and
 * so must be re-authored (not merely re-sized). A segment is flagged when the
 * area set changed AND its text names any previous area — even one that is
 * still targeted, because "Twin Cities adults" is wrong once Charleston and
 * New York are added. Returns segment id -> previous labels it names.
 */
export function segmentsNeedingReauthor(
  segments: AudienceSegment[],
  previousAreas: Pick<GeoArea, 'label'>[],
  nextAreas: Pick<GeoArea, 'label'>[],
): Map<string, string[]> {
  const flagged = new Map<string, string[]>();
  if (sameAreaSet(previousAreas, nextAreas)) return flagged;
  const labels = previousAreas.map((a) => a.label).filter(Boolean);
  if (labels.length === 0) return flagged;
  for (const segment of segments) {
    const named = geographyNamedBySegment(segment, labels);
    if (named.length > 0) flagged.set(segment.id, named);
  }
  return flagged;
}

// ---------------------------------------------------------------------------
// Literal place names in generated copy
// ---------------------------------------------------------------------------

const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
  CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho',
  IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina',
  ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas',
  UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia',
  WI: 'Wisconsin', WY: 'Wyoming',
};

export function stateNameOf(raw: string | undefined): string | null {
  const t = raw?.trim();
  if (!t) return null;
  if (t.length === 2) return STATE_NAMES[t.toUpperCase()] ?? null;
  return Object.values(STATE_NAMES).find((n) => n.toLowerCase() === t.toLowerCase()) ?? null;
}

/** "Minnesota" / "mn" -> "MN" (null when not a US state). */
export function stateAbbrOf(raw: string | undefined): string | null {
  const name = stateNameOf(raw);
  if (!name) return null;
  return Object.keys(STATE_NAMES).find((k) => STATE_NAMES[k] === name) ?? null;
}

/** "Minneapolis, MN" -> "Minnesota" (null when the label carries no state). */
function stateOfLabel(label: string): string | null {
  const parts = label.split(',');
  return parts.length > 1 ? stateNameOf(parts[parts.length - 1]) : null;
}

/**
 * The company-description idiom: "Minneapolis-based", "based in Minneapolis",
 * "headquartered in Minneapolis", "founded in Minneapolis". Where the brand
 * lives is a research fact, not a targeting claim, so it stays legal copy.
 */
function isBrandFact(text: string, variant: string): boolean {
  const v = variant.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(
    `(?:\\b(?:based|headquartered|founded|born|rooted)\\s+(?:in|out of)\\s+${v}\\b|\\b${v}[- ](?:based|born|founded|headquartered)\\b)`,
    'i',
  ).test(text);
}

/**
 * Place names that must never be typed into generated deck copy as literals,
 * because geography is a plan fact that can change ({{geo.areas}} /
 * {{geo.summary}} follow it): every target area's label, city, aliases
 * ("Twin Cities") and state ("Minnesota"), and the advertiser's HQ
 * city/state when it is NOT itself a target (HQ is not the market).
 * Exempt: the advertiser's own name ("Minneapolis Brewing"), and the
 * company-description idiom ("Minneapolis-based") — see isBrandFact.
 * Returns the offending place names (empty when the copy is clean).
 */
export function findLiteralGeography(
  text: string,
  ctx: {
    areas: Array<Pick<GeoArea, 'label'> & Partial<Pick<GeoArea, 'kind'>>>;
    locations: Array<{ label?: string; city?: string; state?: string }>;
    advertiserName?: string | null;
  },
): string[] {
  if (!text.trim()) return [];
  let scrubbed = text;
  if (ctx.advertiserName?.trim()) {
    const esc = ctx.advertiserName.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    scrubbed = scrubbed.replace(new RegExp(esc, 'gi'), ' ');
  }
  const banned = new Set<string>();
  for (const area of ctx.areas) {
    areaNameVariants(area.label).forEach((v) => banned.add(v));
    // A whole-state target ("Minnesota", kind 'state') names its own state.
    const st = stateOfLabel(area.label) ?? ((area as { kind?: string }).kind === 'state' ? stateNameOf(area.label) : null);
    if (st) banned.add(st);
  }
  const targetKeys = new Set([...banned].map((v) => v.toLowerCase()));
  for (const loc of ctx.locations) {
    const city = loc.city?.trim() || (loc.label ? cityPart(loc.label) : '');
    if (city && !targetKeys.has(city.toLowerCase())) {
      areaNameVariants(loc.label || city).forEach((v) => {
        if (!targetKeys.has(v.toLowerCase())) banned.add(v);
      });
      banned.add(city);
    }
    const st = stateNameOf(loc.state) ?? (loc.label ? stateOfLabel(loc.label) : null);
    // A state is HQ-banned only when it is not also a target state.
    if (st && !targetKeys.has(st.toLowerCase())) banned.add(st);
  }
  const found: string[] = [];
  for (const v of banned) {
    if (v.length < 3 || !mentions(scrubbed, v)) continue;
    if (isBrandFact(scrubbed, v)) continue;
    found.push(v);
  }
  return found;
}
